import { z } from 'zod'

/**
 * What the platform can read, and what it learned reading it (PRD-24).
 *
 * The accept list lives here, once, because it used to be copied by hand into two web
 * surfaces and the server's refusal sentence, and three copies of a list drift (PRD-24
 * F-17, D-12). Both the browser's `accept` attribute and the words a person reads come
 * from the same table.
 */

export type SupportedUploadFormat = {
  /** Lower-case extensions, with the dot. */
  extensions: string[]
  /** In the product's words, for a sentence that lists what can be uploaded. */
  label: string
  /** Whether reading it depends on OCR being installed. */
  needsOcr: boolean
}

export const SUPPORTED_UPLOAD_FORMATS: readonly SupportedUploadFormat[] = [
  { extensions: ['.pdf'], label: 'PDF', needsOcr: false },
  { extensions: ['.docx'], label: 'Word', needsOcr: false },
  { extensions: ['.pptx'], label: 'PowerPoint', needsOcr: false },
  { extensions: ['.xlsx'], label: 'Excel', needsOcr: false },
  { extensions: ['.png', '.jpg', '.jpeg', '.tif', '.tiff', '.webp', '.bmp', '.gif'], label: 'images', needsOcr: true },
  { extensions: ['.md', '.markdown'], label: 'Markdown', needsOcr: false },
  { extensions: ['.html', '.htm'], label: 'HTML', needsOcr: false },
  { extensions: ['.json'], label: 'JSON', needsOcr: false },
  { extensions: ['.csv', '.tsv'], label: 'CSV', needsOcr: false },
  { extensions: ['.txt', '.text', '.log'], label: 'plain text', needsOcr: false }
]

/** The value of a file input's `accept` attribute. */
export const SUPPORTED_UPLOAD_ACCEPT: string = SUPPORTED_UPLOAD_FORMATS.flatMap((f) => f.extensions).join(',')

/** "PDF, Word, PowerPoint, Excel, images, Markdown, HTML, JSON, CSV and plain text". */
export const SUPPORTED_FORMATS_SENTENCE: string = (() => {
  const labels = SUPPORTED_UPLOAD_FORMATS.map((f) => f.label)
  return `${labels.slice(0, -1).join(', ')} and ${labels[labels.length - 1]}`
})()

export const isSupportedUploadName = (filename: string): boolean => {
  const lower = filename.toLowerCase()
  return SUPPORTED_UPLOAD_FORMATS.some((f) => f.extensions.some((ext) => lower.endsWith(ext)))
}

// ---------------------------------------------------------------------------
// The ingest report (ING-014)
// ---------------------------------------------------------------------------

export const IngestWarningSchema = z.object({
  code: z.string(),
  message: z.string(),
  count: z.number().int().nonnegative().optional()
})

export const IngestImageReportSchema = z.object({
  found: z.number().int().nonnegative(),
  read: z.number().int().nonnegative(),
  withText: z.number().int().nonnegative(),
  skipped: z.object({
    tiny: z.number().int().nonnegative(),
    duplicate: z.number().int().nonnegative(),
    unsupported: z.number().int().nonnegative(),
    cap: z.number().int().nonnegative(),
    off: z.number().int().nonnegative(),
    unavailable: z.number().int().nonnegative()
  }),
  meanConfidence: z.number().nullable(),
  discardedBlocks: z.number().int().nonnegative()
})

export const IngestReportSchema = z.object({
  /** `v2:pdf`, `v2:docx`, … */
  extractor: z.string(),
  blocks: z.object({
    headings: z.number().int().nonnegative(),
    paragraphs: z.number().int().nonnegative(),
    listItems: z.number().int().nonnegative(),
    tables: z.number().int().nonnegative(),
    images: z.number().int().nonnegative(),
    imagesWithText: z.number().int().nonnegative(),
    notes: z.number().int().nonnegative()
  }),
  pages: z.number().int().nonnegative().optional(),
  slides: z.number().int().nonnegative().optional(),
  sheets: z.array(z.string()).optional(),
  images: IngestImageReportSchema,
  ocr: z
    .object({
      engine: z.string(),
      languages: z.array(z.string()),
      pagesScanned: z.number().int().nonnegative(),
      pagesRasterized: z.number().int().nonnegative(),
      ms: z.number().nonnegative()
    })
    .nullable(),
  hidden: z.object({ slides: z.number().int().nonnegative(), sheets: z.number().int().nonnegative() }),
  warnings: z.array(IngestWarningSchema),
  /** Stage timings, milliseconds. */
  timings: z.object({
    extractMs: z.number().nonnegative(),
    chunkMs: z.number().nonnegative(),
    embedMs: z.number().nonnegative(),
    indexMs: z.number().nonnegative(),
    totalMs: z.number().nonnegative()
  }),
  chunks: z.number().int().nonnegative(),
  language: z.string().nullable(),
  textSearchConfig: z.string()
})
export type IngestReport = z.infer<typeof IngestReportSchema>

export const ChunkKindSchema = z.enum(['prose', 'table', 'image_text', 'notes'])
export type ChunkKind = z.infer<typeof ChunkKindSchema>
